import { Tooltip } from 'antd';
import { format, startOfDay } from 'date-fns';
import { useState } from 'react';
import {
  customMillisecondsToHours,
  shiftTimeFormat,
} from '../../lib/utils/date-time';
import type {
  ShiftPeriod,
  RosterDisplayMode,
  ShiftCellRecord,
  ShiftBackgroundColours,
  FilledShift,
} from './types';
import ShiftForeground from './ShiftForeground';
import ShiftBackground from './ShiftBackground';
import { Optional } from '../Shared';
import { userFullName } from '../../lib/utils';
import {
  minutesPerDay,
  minutesPerHour,
  shiftDivOffsetPx,
} from './lib/constants';
import { differenceInMinutesWithoutTimezone } from './lib/shift-service';
import ShiftDrawer from './Shift/ShiftDrawer';

type ShiftButtonProps = {
  readonly shift: FilledShift;
  readonly cellRecord: ShiftCellRecord;
  readonly rosterDisplayMode: RosterDisplayMode;
  readonly readOnly: boolean;
  readonly dayShift: ShiftPeriod;
  readonly shiftBackgroundColours: ShiftBackgroundColours;
  readonly cellWidth?: number;
  readonly showShiftStatus: boolean;
};

type ShiftButtonStylesParams = {
  readonly rosterDisplayMode: RosterDisplayMode;
  readonly cellDivWidthInPixel: number;
  readonly shiftStartMinutesOfDay: number;
  readonly shiftDurationInMinutes: number;
};

/**
 * Shift button's styles for width and left position (in pixels), so the Shift looks like temporal.
 */
const shiftButtonDynamicStyles = ({
  rosterDisplayMode,
  cellDivWidthInPixel,
  shiftStartMinutesOfDay,
  shiftDurationInMinutes,
}: ShiftButtonStylesParams) => {
  const shiftButtonWidthInPixelPerMinute = cellDivWidthInPixel / minutesPerDay;

  const shiftButtonWidthInPixelPerHour =
    minutesPerHour * shiftButtonWidthInPixelPerMinute;

  // Minimum width of the button, if shift button is less than this width, we don't display hour text, so it is not truncated.
  const shiftButtonMinWidthInPixel = 5 * shiftButtonWidthInPixelPerHour;

  const shiftButtonLeftPositionInPixel =
    shiftStartMinutesOfDay * shiftButtonWidthInPixelPerMinute;

  const shiftButtonWidthInPixel =
    shiftButtonWidthInPixelPerMinute * shiftDurationInMinutes;

  return {
    shiftButtonWidth:
      rosterDisplayMode === 'monthly'
        ? Math.max(shiftButtonMinWidthInPixel, shiftButtonWidthInPixel)
        : shiftButtonWidthInPixel,
    shiftButtonLeftPosition: shiftButtonLeftPositionInPixel + shiftDivOffsetPx,
    hideShiftForeground:
      rosterDisplayMode === 'monthly'
        ? true
        : shiftButtonWidthInPixel < shiftButtonMinWidthInPixel,
  };
};

/**
 * A button with calculated dynamic width and position represents a Shift in a roster table.
 */
const ShiftButton = ({
  shift,
  rosterDisplayMode,
  cellRecord,
  readOnly,
  dayShift,
  shiftBackgroundColours,
  cellWidth,
  showShiftStatus,
}: ShiftButtonProps) => {
  const [showShiftModal, setShowShiftModal] = useState(false);

  const cellDivWidthInPixel = cellWidth ?? 0;

  const startTime = new Date(shift.startTime);

  const endTime = new Date(shift.endTime);

  const shiftDurationInMinutes = differenceInMinutesWithoutTimezone(
    endTime,
    startTime
  );

  // Overflow shift has different start minutes of the day, which is previous day.
  // E.g. overflow shift start time is 08/02/2022 18:00 of previous day, and end time is today 09/02/2022 18:00 with total 24 hours duration.
  // Then the shift "start minutes of day" would be negative: "shift start time" - "start of the day for current date".
  // That would be (08/02/2022 18:00) - (09/02/2022 00:00), difference is -6 hours, which would be -360 minutes.
  const shiftStartMinutesOfDay =
    shift.overflow === true
      ? differenceInMinutesWithoutTimezone(startTime, startOfDay(endTime))
      : differenceInMinutesWithoutTimezone(startTime, startOfDay(startTime));

  const { shiftButtonWidth, shiftButtonLeftPosition, hideShiftForeground } =
    shiftButtonDynamicStyles({
      rosterDisplayMode,
      shiftDurationInMinutes,
      shiftStartMinutesOfDay,
      cellDivWidthInPixel,
    });

  const shiftDurationInHours = customMillisecondsToHours(
    endTime.getTime() - startTime.getTime()
  );

  const shiftDurationString = `${shiftDurationInHours}h`;

  const userName = userFullName({
    first_name: cellRecord.firstName,
    last_name: cellRecord.lastName,
  });

  const isVacant = cellRecord.userRoleAssignmentId === undefined;

  const tooltip = (
    <>
      {`${cellRecord.roleName}`}
      <div className="shift-tooltip-username">{userName}</div>
      {`${format(startTime, shiftTimeFormat)} to ${format(
        endTime,
        shiftTimeFormat
      )} (${shiftDurationString})`}
      <br />
      {`Status: ${shift.status}`}
    </>
  );

  const defaultDayTimeHours = {
    start: customMillisecondsToHours(dayShift.start),
    end: customMillisecondsToHours(dayShift.end),
  };

  const onClickShiftButton = () => {
    setShowShiftModal(true);
  };

  const onShiftButtonKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      setShowShiftModal(true);
    }
  };

  const shiftButtonStyles = {
    padding: 0,
    zIndex: 2,
    border: 'none',
    cursor: 'pointer',
    minHeight: '65px',
    left: shiftButtonLeftPosition,
    width: shiftButtonWidth,
    display: 'block',
  };

  return (
    <>
      <Tooltip
        title={tooltip}
        color="#000000"
        overlayInnerStyle={{ fontWeight: 500 }}
      >
        <div
          className="shift-btn"
          role="button"
          tabIndex={0}
          style={{ ...shiftButtonStyles, position: 'relative' }}
          onClick={onClickShiftButton}
          onKeyUp={onShiftButtonKeyUp}
          title={startTime.toDateString()}
        >
          <div style={{ display: 'block', overflow: 'auto' }}>
            <ShiftBackground
              dayShiftPeriod={dayShift}
              shift={shift}
              shiftBackgroundColours={shiftBackgroundColours}
              isVacant={isVacant}
            />
            <Optional show={!hideShiftForeground}>
              <ShiftForeground
                isVacant={isVacant}
                userInfo={isVacant ? 'Vacant' : `${userName}`}
                shiftStatus={shift.status}
                showShiftStatus={showShiftStatus}
                shiftDuration={shiftDurationString}
                shiftTime={`${format(startTime, shiftTimeFormat)} - ${format(
                  endTime,
                  shiftTimeFormat
                )}`}
              />
            </Optional>
          </div>
        </div>
      </Tooltip>

      <ShiftDrawer
        shift={shift}
        role={cellRecord.roleName}
        cellRecord={readOnly ? undefined : cellRecord}
        open={showShiftModal}
        onClose={() => setShowShiftModal(false)}
        defaultDayTimeHours={defaultDayTimeHours}
        shiftStartTime={shift.startTime}
      />
    </>
  );
};

export default ShiftButton;
